import { Action, Module, Mutation, VuexModule } from "vuex-module-decorators";
import Scouting, { AnalysisState, ScoutingAnalysis } from "@/models/Scouting";
import scoutingService from "@/service/scoutingService";
import ScoutingPlace from "@/models/ScoutingPlace";
import AnalysisService from "@/service/AnalysisService";
import ScoutingArea from "@/models/ScoutingArea";
import MediumCoverage from "@/models/MediumCoverage";
import ScoutingGroup from "@/models/ScoutingGroup";

@Module({
  namespaced: true,
  name: "currentScouting",
})
export default class CurrentScouting extends VuexModule {
  _scouting: Scouting | undefined = undefined;
  _scoutingAnalysis: ScoutingAnalysis | undefined = undefined;
  _scoutingAnalysisPlace: ScoutingPlace | undefined = undefined;
  _scoutingAnalysisArea: ScoutingArea | undefined = undefined;
  _scoutingAnalysisGroup: ScoutingGroup | undefined = undefined;

  _scoutingPlaces: ScoutingPlace[] = [];
  _searchScoutingPlaces: ScoutingPlace[] = [];

  _scoutingAreas: ScoutingArea[] = [];
  _searchScoutingAreas: ScoutingArea[] = [];

  _scoutingSelectedItemId: string | null = null;

  get scoutingSelectedItemId() {
    return this._scoutingSelectedItemId;
  }

  get scouting() {
    return this._scouting;
  }

  get scoutingAnalysis() {
    return this._scoutingAnalysis;
  }

  get scoutingAnalysisPlace() {
    return this._scoutingAnalysisPlace;
  }

  get scoutingAnalysisArea() {
    return this._scoutingAnalysisArea;
  }

  get scoutingAnalysisGroup() {
    return this._scoutingAnalysisGroup;
  }

  get scoutingPlaces() {
    return this._scoutingPlaces || [];
  }

  get searchScoutingPlaces() {
    return this._searchScoutingPlaces || [];
  }

  get scoutingAreas() {
    return this._scoutingAreas || [];
  }

  get searchScoutingAreas() {
    return this._searchScoutingAreas || [];
  }

  get analysisCurrentScoutingPlace() {
    return this._scoutingAnalysisPlace;
  }

  get analysisCurrentScoutingArea() {
    return this._scoutingAnalysisArea;
  }

  get analysisCurrentScoutingGroup() {
    return this._scoutingAnalysisGroup;
  }

  @Mutation
  setScoutingSelectedItemId(id: string) {
    this._scoutingSelectedItemId = id;
  }

  @Mutation
  setScouting(payload: Scouting) {
    this._scouting = payload;
  }

  @Mutation
  setScoutingPlaces(payload: ScoutingPlace[]) {
    this._scoutingPlaces = payload;
  }

  @Mutation
  setScoutingAnalysis(payload: ScoutingAnalysis) {
    this._scoutingAnalysis = payload;
  }

  @Mutation
  addScoutingPlaces(payload: ScoutingPlace[]) {
    this._scoutingPlaces.push(...payload);
  }

  @Mutation
  setScoutingAreas(payload: ScoutingArea[]) {
    this._scoutingAreas = payload;
  }

  @Mutation
  addScoutingAreas(payload: ScoutingArea[]) {
    this._scoutingAreas.push(...payload);
  }

  @Mutation
  addClientScoutingPlace(payload: ScoutingPlace) {
    this._scoutingPlaces.push(payload);
  }

  @Mutation
  updateClientScoutingPlaces() {}

  @Mutation
  $addSearchScoutingPlaces(payload: ScoutingPlace[]) {
    this._searchScoutingPlaces.push(...payload);
  }

  @Mutation
  $addSearchScoutingAreas(payload: ScoutingArea[]) {
    this._searchScoutingAreas.push(...payload);
  }

  @Mutation
  removeScoutingPlace(id: string) {
    const index = this._scoutingPlaces.findIndex((o) => o.id === id);
    if (index != undefined) {
      this._scoutingPlaces.splice(index, 1);
    }
  }

  @Mutation
  removeScoutingArea(id: string) {
    const index = this._scoutingAreas.findIndex((o) => o.id === id);
    if (index != undefined) {
      this._scoutingAreas.splice(index, 1);
    }
  }

  @Mutation
  updateScoutingPlace(payload: ScoutingPlace) {
    const value = this._scoutingPlaces.find((o) => o.id === payload.id);
    Object.assign({}, value, payload);
  }

  @Mutation
  updateScoutingArea(payload: ScoutingArea) {
    const value = this._scoutingAreas.find((o) => o.id === payload.id);
    Object.assign({}, value, payload);
  }

  @Mutation
  $updateSearchScoutingPlace(payload: ScoutingPlace) {
    const value = this._searchScoutingPlaces.find(
      (o) => o.sourceId === payload.sourceId,
    );
    Object.assign({}, value, payload);
  }

  @Mutation
  $updateSearchScoutingArea(payload: ScoutingArea) {
    const value = this._searchScoutingAreas.find((o) => o.id === payload.id);
    Object.assign({}, value, payload);
  }

  @Mutation
  $updateSelectionScoutingPlace(payload: ScoutingPlace) {
    let value = this._searchScoutingPlaces.find((o) => o.id === payload.id);
    if (value) value.highlighted = payload.highlighted;
  }

  @Mutation
  $updateSelectionScoutingArea(payload: ScoutingArea) {
    let value = this._searchScoutingAreas.find((o) => o.id === payload.id);
    if (value) value.highlighted = payload.highlighted;
  }

  @Mutation
  $clearMarkers() {}

  @Mutation
  $saveScouting() {}

  @Mutation
  $centerOnMarker(scoutingPlace: ScoutingPlace) {}

  @Mutation
  $centerOnArea(scoutingArea: ScoutingArea) {}

  @Mutation
  $centerOnMarkers() {}

  @Mutation
  $centerOnAllObjects() {}

  @Mutation
  $setSearchScoutingPlaces(payload: ScoutingPlace[]) {
    this._searchScoutingPlaces = payload;
  }

  @Mutation
  $setSearchScoutingAreas(payload: ScoutingArea[]) {
    this._searchScoutingAreas = payload;
  }

  @Mutation
  $changeScoutingAnalysisState(state: AnalysisState) {
    if (this._scoutingAnalysis) {
      this._scoutingAnalysis.status = state;
      this._scoutingAnalysisArea = undefined;
      this._scoutingAnalysisPlace = undefined;
      this._scoutingAnalysisGroup = undefined;
    }
  }

  @Mutation
  $changeScoutingAnalysisCurrentPlace(scoutingPlace: ScoutingPlace) {
    this._scoutingAnalysisPlace = scoutingPlace;
  }

  @Mutation
  $changeScoutingAnalysisCurrentArea(scoutingArea: ScoutingArea) {
    this._scoutingAnalysisArea = scoutingArea;
  }

  @Mutation
  $changeScoutingAnalysisCurrentGroup(scoutingGroup: ScoutingGroup) {
    this._scoutingAnalysisGroup = scoutingGroup;
  }

  @Mutation
  $clearSelectedScoutingArea() {
    this._scoutingAnalysisArea = undefined;
  }

  @Mutation
  $clearSelectedScoutingPlace() {
    this._scoutingAnalysisPlace = undefined;
  }

  @Mutation
  $clearSelectedScoutingGroup() {
    this._scoutingAnalysisGroup = undefined;
  }

  @Action({ rawError: true })
  async saveScoutingPlace(place: ScoutingPlace) {
    const { commit } = this.context;
    const payload = place.toJson;
    const resp = await scoutingService.updateScoutingPlace(payload);
    commit("updateScoutingPlace", resp);
  }

  @Action({ rawError: true })
  async saveScoutingPlaceOffline(place: ScoutingPlace) {
    const { commit } = this.context;
    commit("updateScoutingPlace", place);
  }

  @Action({ rawError: true })
  async destroyScoutingPlace(id: string) {
    const { commit } = this.context;
    const resp = await scoutingService.destroyScoutingPlace(id);
    commit("removeScoutingPlace", id);
  }

  @Action({ rawError: true })
  async destroyScoutingArea(id: string) {
    const { commit } = this.context;
    const resp = await scoutingService.destroyScoutingArea(id);
    commit("removeScoutingArea", id);
  }

  @Action({ rawError: true })
  async saveScoutingPlaces(places: ScoutingPlace[]) {
    const placesToSave = places.filter((place) => {
      return !this._scoutingPlaces.find((sp) => sp.sourceId === place.sourceId);
    });
    const { commit } = this.context;
    const payloads = placesToSave.map((o) => o.toJson);
    const resp = await scoutingService.createScoutingPlaces({
      places: payloads,
    });
    commit("addScoutingPlaces", places);
    commit("$setSearchScoutingPlaces", []);
  }

  @Action({ rawError: true })
  async saveScoutingAreas(areas: ScoutingArea[]) {
    const objsToSave = areas.filter((area) => {
      return !this._scoutingAreas.find(
        (o) =>
          ScoutingArea.makeKey(o.sourceableId, o.sourceableType) ===
          ScoutingArea.makeKey(area.sourceableId, area.sourceableType),
      );
    });
    const { commit } = this.context;
    const objs = [];
    for (const area of areas) {
      const obj = await scoutingService.createScoutingArea(area);
      objs.push(obj);
    }
    commit("addScoutingAreas", objs);
    commit("$setSearchScoutingAreas", []);
  }

  @Action({ rawError: true })
  async addClientScoutingPlaces(places: ScoutingPlace[]) {
    const { commit } = this.context;
    const payloads = places.map((o) => o.toJson);

    for (let i = 0; i < places.length; i++) {
      if (
        this._scoutingPlaces.find(
          (o) =>
            (places[i].id && o.id === places[i].id) ||
            (places[i].sourceId && o.sourceId === places[i].sourceId) ||
            (places[i].position &&
              o.position.lat === places[i].position.lat &&
              o.position.lng === places[i].position.lng),
        )
      ) {
        continue;
      }

      const res = await scoutingService.createScoutingPlace(places[i]);
      commit("addClientScoutingPlace", res);
    }

    commit("updateClientScoutingPlaces");
  }

  @Action({ rawError: true })
  async resetScouting() {
    const { commit } = this.context;
    const resp = await scoutingService.resetScouting();
    commit("setScouting", resp);
    commit("setScoutingPlaces", []);
    commit("setScoutingAreas", []);
    commit("$clearMarkers");
  }

  @Action({ rawError: true })
  addSearchScoutingPlaces(payload: ScoutingPlace[]) {
    const { commit } = this.context;
    commit("$addSearchScoutingPlaces", payload);
  }

  @Action({ rawError: true })
  addSearchScoutingAreas(payload: ScoutingArea[]) {
    const { commit } = this.context;
    commit("$addSearchScoutingAreas", payload);
  }

  @Action({ rawError: true })
  updateSearchScoutingPlace(payload: ScoutingPlace) {
    const { commit } = this.context;
    commit("$updateSearchScoutingPlace", payload);
  }

  @Action({ rawError: true })
  updateSearchScoutingArea(payload: ScoutingArea) {
    const { commit } = this.context;
    commit("$updateSearchScoutingArea", payload);
  }

  @Action({ rawError: true })
  updateSelectionScoutingPlace(payload: ScoutingPlace) {
    const { commit } = this.context;
    commit("$updateSelectionScoutingPlace", payload);
  }

  @Action({ rawError: true })
  updateSelectionScoutingArea(payload: ScoutingArea) {
    const { commit } = this.context;
    commit("$updateSelectionScoutingArea", payload);
  }

  @Action({ rawError: true })
  setSearchScoutingPlaces(payload: ScoutingPlace[]) {
    const { commit } = this.context;
    commit("$setSearchScoutingPlaces", payload);
  }

  @Action({ rawError: true })
  async saveScouting(scouting: Scouting) {
    const { commit } = this.context;
    const payload = {
      id: scouting.id,
      isocronType: scouting.isocronType,
      isocronRange: scouting.isocronRange,
      isocronMeansType: scouting.isocronMeansType,
    };
    const resp = await scoutingService.updateScouting(payload);
    commit("$saveScouting");
    commit("setScouting", resp);
  }

  @Action({ rawError: true })
  async loadCurrentScouting(options: any = {}) {
    const { commit } = this.context;
    try {
      const resp = await scoutingService.getCurrentScouting();

      commit("setScouting", resp);
      return resp;
    } catch (e) {
      commit("setScouting", undefined);
    }
  }

  @Action({ rawError: true })
  async clearMarkers() {
    const { commit } = this.context;
    commit("$clearMarkers");
  }

  @Action({ rawError: true })
  centerOnMarkers() {
    const { commit } = this.context;
    commit("$centerOnMarkers");
  }

  @Action({ rawError: true })
  async centerOnMarker(scoutingPlace: ScoutingPlace) {
    const { commit } = this.context;
    commit("$centerOnMarker", scoutingPlace);
  }
  @Action({ rawError: true })
  async centerOnArea(scoutingArea: ScoutingArea) {
    const { commit } = this.context;
    commit("$centerOnArea", scoutingArea);
  }
  @Action({ rawError: true })
  async centerOnAllObjects() {
    const { commit } = this.context;
    commit("$centerOnAllObjects");
  }

  @Action({ rawError: true })
  async loadScoutingPlaces(options: any = {}) {
    const { commit } = this.context;
    try {
      const resp = await scoutingService.getScoutingPlaces();
      resp.forEach((o: ScoutingPlace) => (o.highlighted = true));
      commit("setScoutingPlaces", resp);
      return resp;
    } catch (e) {
      commit("setScoutingPlaces", []);
    }
  }

  @Action({ rawError: true })
  async loadScoutingAreas(options: any = {}) {
    const { commit } = this.context;
    try {
      const resp = await scoutingService.getScoutingAreas();
      resp.forEach((o: ScoutingArea) => (o.highlighted = true));
      commit("setScoutingAreas", resp);
      return resp;
    } catch (e) {
      commit("setScoutingAreas", []);
    }
  }

  @Action({ rawError: true })
  async loadScoutingAnalysis(scoutingId: string) {
    const { commit } = this.context;
    try {
      const analysis: ScoutingAnalysis = new ScoutingAnalysis();
      analysis.scouting = (await AnalysisService.getAnalysis(
        scoutingId,
      )) as Scouting;

      const mediumCoverageList =
        await AnalysisService.getSourceableMediumCoverages(
          "Scouting",
          analysis.scouting.id,
        );
      analysis.scouting.mediumCoverages =
        mediumCoverageList as MediumCoverage[];

      analysis.locationCounter =
        (await AnalysisService.getLocationCounters()) as any;
      analysis.totalRegions = analysis.locationCounter.totalRegions || 0;
      analysis.totalDistricts = analysis.locationCounter.totalDistricts || 0;
      analysis.totalTowns = analysis.locationCounter.totalTowns || 0;

      commit("setScoutingAnalysis", analysis);
      return analysis;
    } catch (e) {
      commit("setScoutingAnalysis", undefined);
    }
  }

  @Action({ rawError: true })
  async resetScoutingAnalysis() {
    const { commit } = this.context;
    commit("$changeScoutingAnalysisCurrentPlace", undefined);
    commit("$changeScoutingAnalysisCurrentArea", undefined);
    commit("$changeScoutingAnalysisCurrentGroup", undefined);
    commit("setScoutingAnalysis", undefined);
  }

  @Action({ rawError: true })
  async changeScoutingAnalysisState(state: AnalysisState) {
    const { commit } = this.context;
    commit("$changeScoutingAnalysisState", state);
  }

  @Action({ rawError: true })
  async changeScoutingAnalysisCurrentPlace(scoutingPlace: ScoutingPlace) {
    const { commit } = this.context;
    commit("$changeScoutingAnalysisCurrentPlace", scoutingPlace);
  }

  @Action({ rawError: true })
  async changeScoutingAnalysisCurrentArea(scoutingArea: ScoutingArea) {
    const { commit } = this.context;
    commit("$changeScoutingAnalysisCurrentArea", scoutingArea);
  }

  @Action({ rawError: true })
  async changeScoutingAnalysisCurrentGroup(scoutingGroup: ScoutingGroup) {
    const { commit } = this.context;
    commit("$changeScoutingAnalysisCurrentGroup", scoutingGroup);
  }

  @Action({ rawError: true })
  async clearSelection() {
    const { commit } = this.context;
    commit("$clearSelectedScoutingArea");
    commit("$clearSelectedScoutingPlace");
    commit("$clearSelectedScoutingGroup");
  }
}
